/* eslint-disable @typescript-eslint/no-unused-vars */

import { EditClientModal } from "../../Clients/EditClientModal";
import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Config from "../../../Utils/Config";
import { Vocabulary } from "../../../Utils/Vocabulary";
import { ClientPanelProps } from "../../../Utils/Types";
import styles from "../../../Styles/clientPanel.module.css";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  formatDate,
  genericEmptyField,
  getFullName,
  getRestrictedUserWorkPointId,
  isMobile,
  isString,
  OrderStatus,
  OrderStatusType,
} from "../../../Utils/Utils";
import SchoolIcon from "@mui/icons-material/School";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { LicenceTypeProductInfo } from "./LIcenceTypeProductInfo";
import { endpoints } from "../../../Utils/UrlEnum";
import { AxiosResponse } from "axios";
import { ClassModel, CompanyModel, VehicleModel } from "../../../Utils/Models";
import { getData } from "../../../Services/getData";
import ModalButton from "../../ModalButton";
import EditClass from "../../Classes/EditClass";
import EditCompany from "../../Companies/EditCompany";
import { EditInstructor } from "../EditInstructor";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import PostAddIcon from "@mui/icons-material/PostAdd";
import ClientHistory from "./ClientHistory";
import {
  FormsIds,
  ExternalLinks,
  RomanianCounties,
} from "../../../Utils/Constants";
import EditIcon from "@mui/icons-material/Edit";
import { Controller } from "react-hook-form";
import CollectPaymentForm from "../../Payments/CollectPaymentForm";
import withRole from "../../../Hooks/withRole";
import { NumericFormatCustom } from "../../../Utils/NumericFormatComponents";
import ARRNumber from "../../ARRNumber/ARRNumber";
import ARRAdmissionDatePicker from "../../ARRNumber/ARRAdmissionDatePicker";
import { postData } from "../../../Services/postData";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import GenericModal from "../../GenericModal";
import SellBookButton from "../../Books/SellBookButton";
import ReplayIcon from "@mui/icons-material/Replay";
import EventIcon from "@mui/icons-material/Event";
import useCustomMemo from "../../../Hooks/useCustomMemo";

function ClientPanel(props: ClientPanelProps) {
  const {
    disabled,
    getValues,
    control,
    saveOrder,
    products,
    instructors,
    allDocumentTypes,
    collectPaymentType,
    setTriggerReload,
    triggerReload,
    clientDocuments,
    onDocumentSaved,
    hasReadiness,
    readinessProgress,
    clientReadinessData,
  } = props;
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const [vehicles, setVehicles] = useState([] as VehicleModel[]);
  const [classes, setClasses] = useState([] as ClassModel[]);
  const companies =
    JSON.parse(localStorage.getItem("storage") || "{}")?.companies ?? [];

  const payedAmount = getValues("amountPaid") || "0"; // Amount paid
  const productPrice = parseFloat(getValues("product")?.price ?? "0"); // Product price

  // Assuming remainingAmount and amountPaidToInstructor need to be calculated or passed as variables
  const remainingAmount = productPrice - payedAmount;
  const amountPaidToInstructor = getValues("amountPaidToInstructor"); // You can calculate this if needed

  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const permissionToChangeData = !props?.restrictAccess;
  const [latestExam, setLatestExam] = useState<any>(getValues("exam"));
  const [certificateExamDates, setCertificateExamDates] = useState<any[]>([]);
  const [openSetsDialog, setOpenSetsDialog] = useState(false);
  const clientId = getValues("client_id._id");
  const memo = useCustomMemo();
  const currentWorkPointId = localStorage.getItem("userWorkPointId");
  const currentWorkPoint = memo?.storage?.workPoints?.find(
    (wp: any) => wp._id === currentWorkPointId,
  );

  /**
   * Send a request to generate a user account for the current client.
   * @remarks The server will generate a user account and return the user data.
   * @returns {Promise<AxiosResponse>} A promise with the response.
   */
  const handleGenerateUserAccount = () => {
    postData(`${endpoints.client.generateUserAccount}`, {
      clientId: getValues("client_id._id"),
    }).then((res: AxiosResponse) => {
      if (!res?.data) return;
      setTriggerReload(!triggerReload);
    });
  };

  /**
   * set vehicles on change of instructor
   */
  useEffect(() => {
    const instructorId = getValues("instructor_id._id");
    if (!instructorId) return;
    getData(
      `${endpoints.vehicle.main}/instructor/paginated?page=0&perPage=100000&id=${instructorId}`,
    ).then((res: AxiosResponse) => {
      if (!res?.data) return;
      const vehicles = res.data?.results;
      if (!vehicles) return;

      const productAutomata = getValues("product.automata");
      const filteredVehicles = vehicles.filter(
        (vehicle: VehicleModel) => vehicle.automata === productAutomata,
      );
      setVehicles(filteredVehicles);

      if (!getValues("vehicle_id")) {
        //check if only one vehicle available or check the first like the manual/automatic and set that
        if (vehicles?.length >= 1) {
          setValue("vehicle_id._id", vehicles[0]._id);
        }
      }
    });
  }, [getValues("instructor_id")]);

  /**
   * Fetch available classes when the product or work point changes.
   * Users restricted to a work point (Functionar) always see classes for
   * their own work point, regardless of the client's work point.
   */
  useEffect(() => {
    //get classes
    const workPointId =
      getRestrictedUserWorkPointId() || getValues("client_id.workPointId._id");
    if (getValues("status.typeId") && workPointId) {
      getData(
        `${endpoints.classesEndPoint}/${getValues("status.typeId")}/${workPointId}`,
      ).then((res: AxiosResponse) => {
        if (!res?.data) return;
        const dateRegex = /(\d{2})\.(\d{2})\.(\d{4})$/;
        const sorted = [...res.data].sort((a: any, b: any) => {
          const getDate = (item: any): number => {
            if (item.startDate) return new Date(item.startDate).getTime();
            const match = item.name?.match(dateRegex);
            if (match)
              return new Date(`${match[3]}-${match[2]}-${match[1]}`).getTime();
            return Infinity;
          };
          return getDate(a) - getDate(b);
        });
        setClasses(sorted);
      });
    }
  }, [getValues("status.typeId")]);

  /**
   * Fetch available exam dates if the product is a certificate.
   */
  useEffect(() => {
    //if the client has a product that is certificate, we need to fetch availble exam dates based on his workpoint
    if (getValues("product.isCertificate")) {
      const now = new Date().toISOString(); // filter exams from now onwards
      getData(`${endpoints.exams.certificateExams}/?fromDate=${now}`).then(
        (res: AxiosResponse) => {
          if (!res?.data) return;
          setCertificateExamDates(res.data);
        },
      );
    }
  }, [getValues("product.isCertificate")]);

  /**
   * Fetch the latest exam for the client
   */
  useEffect(() => {
    setLatestExam(getValues("exam"));
  }, [getValues("exam")]);

  /**
   * renders the title of the collect payment form.
   * if collectPaymentType is a string it will be rendered as a red colored text.
   * otherwise it will not be rendered.
   * @returns {JSX.Element} the rendered title
   */
  const renderSubtitleForModal = () => {
    if (isString(collectPaymentType)) {
      return (
        <Typography
          color="white"
          variant="h6"
        >{`( ${collectPaymentType} )`}</Typography>
      );
    }
  };

  const retryExam = async (exam: any) => {
    //check product type
    const product = getValues("product");

    if (product?.isCertificate) {
      return assignAnotherCertificateExamDate(exam);
    }

    const url = `${endpoints.exams.main}`;
    const res = (await postData(url, exam)) as any;
    if (!res || res?.error) {
      return;
    }
    setTriggerReload(!triggerReload);
  };

  /**
   * assigns a new certificate exam date to a client.
   * @param {any} exam the exam with the new date
   * @returns {Promise<void>}
   */
  const assignAnotherCertificateExamDate = (exam: any) => {
    const url = `${endpoints.exams.changeCertificateExamDate}`;
    const originalExamId = getValues("exam._id");
    const orderId = getValues("_id");

    postData(url, {
      originalExamId,
      clientId,
      newExamId: exam,
      orderId,
    }).then((res: AxiosResponse) => {
      if (!res?.data) return;
      setTriggerReload(!triggerReload);
    });
  };
  /**
   *
   * @param name
   * @param value
   */
  const setValue = (name: string, value: any) => {
    props.setValue(name, value, {
      shouldDirty: true,
    });
  };

  /**
   * Represents an empty row, used to add spacing or separation between client information.
   * @type {{ label: null, value: null }}
   */
  const emptyRow = [
    {
      label: null,
      value: null,
    },
    {
      label: null,
      value: null,
    },
  ];

  /**
   * A list of client information, used to display totals, such as the amount received and the remaining amount.
   * @type {{ label: string, value: string }[]}
   */
  const totals = [
    {
      label: Vocabulary.amountReceived + " / " + Vocabulary.totalAmount,
      value: `${payedAmount} / ${productPrice} ${Vocabulary.lei}`,
      ...(permissionToChangeData && props.editPrice
        ? {
            button: (
              <ModalButton
                buttonIcon={<EditIcon />}
                buttonTitle={Vocabulary.editPrice}
                saveAction={saveOrder}
                content={
                  <Controller
                    name="product.price"
                    control={control}
                    render={({
                      field: { ref, ...field },
                      fieldState: { error },
                    }) => (
                      <TextField
                        {...field}
                        value={field?.value || ""}
                        label={Vocabulary.pret}
                        id="product.price"
                        fullWidth
                        variant="standard"
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          endAdornment: (
                            <InputAdornment position="end">
                              {Vocabulary.currency}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                }
              />
            ),
          }
        : {}),
    },
    {
      label: Vocabulary.remainingAmount,
      value: `${remainingAmount} ${Vocabulary.lei}`, // formatted to 2 decimal places
      highlighted: true,
    },
    {
      label: Vocabulary.amountPaidToInstructor,
      value: `${amountPaidToInstructor} ${Vocabulary.lei}`, // formatted to 2 decimal places
      highlighted: true,
    },
    {
      label: Vocabulary.drivingHours + " / " + Vocabulary.totalDrivingHours,
      value: (
        <strong>{`${
          getValues("nrOfDrivingHours") ?? 0
        }  / ${Number(getValues("product.defaultDrivingHours")) + Number(getValues("supplementaryHours") ?? 0)} ${
          Vocabulary.sessions
        }`}</strong>
      ),
      ...(props?.editDrivingHours && {
        button: (
          <ModalButton
            buttonIcon={<EditIcon />}
            buttonTitle={Vocabulary.editHours}
            saveAction={saveOrder}
            content={
              <Controller
                name="product.defaultDrivingHours"
                control={control}
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{ inputComponent: NumericFormatCustom }}
                  />
                )}
              />
            }
          />
        ),
      }),
    },
  ];

  /**
   * A list of client information, used to display the details of the client.
   * @type {{ label: string, value: string, highlighted?: boolean }[]}
   */
  const clientInfo = [
    {
      label: Vocabulary.workId,
      value: getValues("orderNumber"),
    },
    {
      label: Vocabulary.orderCreatedAt,
      value: formatDate(getValues("createdAt")),
    },
    ...emptyRow,
    {
      label: Vocabulary.CNP,
      value: getValues("client_id.CNP"),
      highlighted: true,
    },
    {
      label: Vocabulary.name,
      value:
        getValues("client_id.lastName") +
        " " +
        getValues("client_id.firstName"),
    },
    {
      label: Vocabulary.phone,
      value: getValues("client_id.phone"),
    },
    {
      label: Vocabulary.email,
      value: getValues("client_id.email"),
    },
    {
      label: Vocabulary.recomendedBy,
      value: (() => {
        const recommendation = getValues("recommendation_id");
        const username =
          recommendation?.user_id?.username ||
          recommendation?.client_id?.firstName +
            " " +
            recommendation?.client_id?.lastName;
        const recommendationValue = recommendation?.recommendationValue;

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {recommendationValue ? (
              <>
                <AccountBoxIcon
                  style={{
                    marginRight: "8px",
                    color: theme.palette.iconBlue?.main,
                  }}
                />
                <span>{`${username} - ${recommendationValue} ${Vocabulary.lei}`}</span>
              </>
            ) : (
              genericEmptyField
            )}
          </div>
        );
      })(),
    },
    ...emptyRow,

    {
      label: Vocabulary.company,
      value:
        companies.find(
          (company: CompanyModel) => company._id === getValues("companyId"),
        )?.name || genericEmptyField,
      ...(permissionToChangeData
        ? {
            button: (
              <ModalButton
                buttonIcon={null}
                buttonTitle={Vocabulary.selectCompany}
                saveAction={saveOrder}
                content={
                  <EditCompany companies={companies} control={control} />
                }
              />
            ),
          }
        : {}),
    },
    ...(getValues("product") && getValues("product.isCertificate")
      ? [
          {
            label: Vocabulary.exam,
            value: formatDate(getValues("exam.examDate")),
            button: (
              <ModalButton
                buttonTitle={
                  latestExam ? Vocabulary.retryExam : Vocabulary.addExam
                }
                buttonIcon={<ReplayIcon />}
                saveAction={() => retryExam(latestExam)}
                content={
                  <FormControl fullWidth>
                    <InputLabel id="exam-date-label">
                      {Vocabulary.examDate}
                    </InputLabel>
                    <Select
                      label={Vocabulary.examDate}
                      id="exam-date"
                      value={latestExam?._id || latestExam} // store the full object
                      onChange={(event) => setLatestExam(event.target.value)}
                    >
                      {certificateExamDates.map((exam, index) => (
                        <MenuItem key={index} value={exam._id}>
                          {`${formatDate(exam.examDate)} (${
                            exam?.workPointId?.name
                          })`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                }
              />
            ),
          },
          {
            label: Vocabulary.arrAdmissionDate,
            value:
              formatDate(getValues("ARRAdmissionDate")) || genericEmptyField,
            button: permissionToChangeData ? (
              <ModalButton
                buttonIcon={null}
                buttonTitle={
                  getValues("ARRAdmissionDate")
                    ? Vocabulary.edit
                    : Vocabulary.add
                }
                saveAction={saveOrder}
                content={<ARRAdmissionDatePicker control={control} />}
              />
            ) : null,
          },
        ]
      : [
          {
            label: Vocabulary.class,
            value: getValues("classId.name") || genericEmptyField,
            button:
              permissionToChangeData || !getValues("classId.name") ? (
                <ModalButton
                  buttonIcon={null}
                  buttonTitle={Vocabulary.selectClass}
                  saveAction={saveOrder}
                  content={<EditClass classes={classes} control={control} />}
                />
              ) : null,
          },
        ]),
    ...(!getValues("product.isCertificate")
      ? [
          {
            label: Vocabulary.ARRNr,
            value: getValues("ARRNumber") || genericEmptyField,
            button:
              permissionToChangeData || !getValues("ARRNumber") ? (
                <ModalButton
                  buttonIcon={null}
                  buttonTitle={
                    getValues("ARRNumber")
                      ? Vocabulary.editArrNumber
                      : Vocabulary.addArrNumber
                  }
                  saveAction={saveOrder}
                  content={
                    <ARRNumber control={control} getValues={getValues} />
                  }
                />
              ) : null,
          },
        ]
      : []),
    {
      label: Vocabulary.employee,
      value: (
        <div style={{ display: "flex", alignItems: "center" }}>
          <AccountBoxIcon
            style={{
              marginRight: "8px",
              color: theme.palette.iconBlue?.main,
            }}
          />
          {getValues("createdBy")}
        </div>
      ),
    },
    ...emptyRow,
    {
      label: Vocabulary.workPoint,
      value: getValues("client_id.workPointId.name"),
    },
    ...(getValues("product.isCertificate")
      ? [
          {
            label: Vocabulary.instructor,
            value: (
              <div style={{ display: "flex", alignItems: "center" }}>
                <AccountBoxIcon
                  style={{
                    marginRight: "8px",
                    color: theme.palette.iconBlue?.main,
                  }}
                />
                {(() => {
                  const firstName =
                    getValues("instructor_id.user_id.firstName") || "";
                  return firstName
                    ? getFullName(getValues("instructor_id.user_id"))
                    : Vocabulary.selectInstructor;
                })()}
              </div>
            ),
            button: (
              <ModalButton
                buttonIcon={null}
                buttonTitle={Vocabulary.addInstructor}
                modalTitle={Vocabulary.selectInstructor}
                saveAction={saveOrder}
                content={
                  <EditInstructor
                    searchUrl={`${endpoints.instructor.user}/paginated?page=0&perPage=100000`}
                    getValues={getValues}
                    control={control}
                    onProductChange={async (
                      _e: any,
                      _product: any,
                      _reason: any,
                      details: any,
                    ) => {
                      const userId = details.option._id;
                      const instructorRes: AxiosResponse = await getData(
                        `${endpoints.instructor.main}/by-user/${userId}`,
                      );
                      if (instructorRes?.data?._id) {
                        setValue("instructor_id", instructorRes.data._id);
                        setValue("vehicle_id", null);
                      }
                    }}
                    instructors={instructors as any}
                  />
                }
              />
            ),
          },
        ]
      : []),
    ...(!getValues("product.isCertificate")
      ? LicenceTypeProductInfo({
          getValues,
          theme,
          setValue,
          control,
          emptyRow,
          instructors,
          vehicles,
          saveOrder,
          exam: getValues("exam"),
          retryExam: retryExam,
          clientDocuments,
          onDocumentSaved,
          productData: getValues("product"),
          allDocumentTypes,
        })
      : []),
    ...emptyRow,
  ];

  /**
   * A function that renders the client information section of the client panel.
   * @returns A JSX element containing the client information.
   */
  const renderClientInfo = () => {
    return (
      <>
        <Grid container spacing={1}>
          {clientInfo.map((item, index) => (
            <Grid
              key={index}
              item
              xs={12}
              sm={12}
              md={12}
              style={{ display: "flex" }}
            >
              <Grid item xs={6} sm={12} md={3}>
                <Typography
                  color={
                    item.label == OrderStatus.practical ||
                    item.label == OrderStatus.theoretical
                      ? "error"
                      : ""
                  }
                  fontWeight={
                    item.label == OrderStatus.practical ||
                    item.label == OrderStatus.theoretical
                      ? "bold"
                      : ""
                  }
                  variant="body1"
                >
                  {item.label}
                  {item.label ? ":" : ""}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={9} className={styles.group}>
                <Typography
                  variant="body1"
                  className={
                    "highlighted" in item && item.highlighted
                      ? styles.highlightedText
                      : styles.text
                  }
                >
                  {item?.value}
                </Typography>
                {"button" in item && item.button ? item.button : null}
              </Grid>
            </Grid>
          ))}

          {totals.map((item, index) => (
            <Grid
              key={index}
              item
              xs={12}
              sm={12}
              md={12}
              style={{ display: "flex" }}
            >
              <Grid item xs={6} sm={12} md={5}>
                <Typography variant="subtitle1">
                  {item.label}
                  {item.label ? ":" : ""}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={12} md={7} className={styles.group}>
                <Typography
                  variant="subtitle1"
                  className={
                    item.highlighted ? styles.highlightedNumber : styles.text
                  }
                >
                  {item.value}
                </Typography>
                {"button" in item && item.button ? item.button : null}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </>
    );
  };

  const renderTitle = () => {
    const isCertificate = getValues("product.isCertificate");

    if (isCertificate) {
      return (
        <>
          <SchoolIcon
            className={styles.icon}
            fontSize="large"
            color="secondary"
          />
          <div
            style={{
              color: theme.palette.secondary?.main,
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            {OrderStatusType.Certification + " - " + getValues("product.name")}
          </div>
        </>
      );
    } else {
      return (
        <>
          <CreditCardIcon
            className={styles.icon}
            fontSize="large"
            color="error"
          />
          <div className={styles.title}>
            {OrderStatusType.License + " - " + getValues("product.name")}
          </div>
        </>
      );
    }
  };

  function updateClientToAutomata() {
    const orderId = getValues("_id");

    postData(`${endpoints.updateClientToAutomata}/${orderId}`, {}).then(
      (res: AxiosResponse) => {
        if (res?.data) {
          setTriggerReload(!triggerReload);
        }
      },
    );
  }

  /**
   * A function that renders the client panel component.
   * @returns A JSX element containing the client panel.
   */
  return (
    <Grid
      container
      spacing={Config.standardGridSpacing}
      className={styles.mainContainer}
    >
      <Grid item xs={12} sm={12} md={12} className={styles.grid}>
        {renderTitle()}
      </Grid>
      {permissionToChangeData && (
        <Grid item xs={12} sm={12} md={6} className={styles.grid}>
          <Button
            startIcon={<GroupAddIcon />}
            variant="contained"
            color="secondary"
            onClick={handleGenerateUserAccount}
            style={{
              color: getValues("client_id.hasPassword") ? "black" : "white",
            }}
            disabled={getValues("client_id.hasPassword")}
          >
            {getValues("client_id.hasPassword")
              ? Vocabulary.clientAlreadyHasUserAccount
              : Vocabulary.generateUserAccount}
          </Button>
        </Grid>
      )}
      {permissionToChangeData && (
        <Grid item xs={12} sm={12} md={6} className={styles.grid}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenConfirmationModal(true)}
            disabled={getValues("product.automata")}
            sx={{
              color: getValues("product.automata") ? "black" : "white",
              "&.Mui-disabled": {
                color: "black",
                backgroundColor: theme.palette.action.disabledBackground,
              },
            }}
          >
            {Vocabulary.isAutomata}
          </Button>
        </Grid>
      )}

      {disabled ? (
        <>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              id="company"
              name="company"
              label={Vocabulary.company}
              value={props.getValues("company") || "-"}
              fullWidth
              variant="standard"
              disabled={disabled}
            />
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12} sm={12} md={6}>
            <EditClientModal
              control={props.control}
              formState={props.formState}
              setValue={props.setValue}
              clearErrors={props.clearErrors}
              getValues={props.getValues}
              open={open}
              setOpen={setOpen}
              onClientChange={(e, client, reason, details) => {
                if (reason !== "clear") {
                  setTriggerReload(!triggerReload);
                }
              }}
              disabled={false}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={12} md={12}>
        {renderClientInfo()}
      </Grid>
      <Divider sx={{ width: "100%", padding: "10px 0" }} />
      {props?.showEditClientButton !== false && (
        <Grid item xs={12} sm={12} md={12}>
          <Button
            startIcon={<AccountBoxIcon />}
            variant="contained"
            color="secondary"
            style={{ color: "white" }}
            onClick={() => setOpen(true)}
          >
            {Vocabulary.editClient}
          </Button>
        </Grid>
      )}
      {props?.showCollectPaymentButton && (
        <>
          <Divider sx={{ width: "100%", padding: "10px 0" }} />
          <Grid
            style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
            item
            xs={12}
            sm={12}
            md={12}
          >
            <ModalButton
              buttonTitle={Vocabulary.collectPayment}
              buttonIcon={<LocalAtmIcon />}
              content={
                <CollectPaymentForm
                  setTriggerReload={setTriggerReload}
                  name={"CollectPaymentForm"}
                  collectPaymentType={collectPaymentType}
                  workPoint={currentWorkPoint}
                  clientId={getValues("client_id._id")}
                  onClose={() => setTriggerReload(!triggerReload)}
                  maxAmount={remainingAmount}
                />
              }
              buttonColor="error"
              buttonSize="medium"
              modalSubtitle={renderSubtitleForModal()}
              formId={FormsIds.collectPayment}
              fullWidth={isMobile()}
            />

            <ModalButton
              buttonColor="secondary"
              buttonTitle={Vocabulary.addNewProductToClient}
              content={
                <ClientHistory
                  getCurrentValues={getValues}
                  products={products}
                />
              }
              formId={FormsIds.addNewProductToClient}
              buttonIcon={<PostAddIcon />}
              fullWidth={isMobile()}
            />
            {getValues("client_id._id") && (
              <SellBookButton
                name="SellBookButton"
                onClose={() => setTriggerReload(!triggerReload)}
                clientId={getValues("client_id._id")}
                fullWidth={isMobile()}
              />
            )}
          </Grid>

          {!getValues("product.isCertificate") && permissionToChangeData && (
            <Grid item xs={12} sm={12} md={6} style={{ paddingTop: "10px" }}>
              <Button
                startIcon={<EventIcon />}
                variant="contained"
                color="success"
                onClick={() => {
                  const workPointName = getValues("client_id.workPointId.name");
                  const county = RomanianCounties.find(
                    (c) =>
                      c.name.toLowerCase() === workPointName?.toLowerCase(),
                  );
                  const countyCode = county?.code || 33;
                  const url = `${
                    ExternalLinks.theoreticalExamSchedulingBase
                  }/${countyCode}/theoryExamination?id=${getValues("_id")}`;
                  window.open(url, "_blank");
                }}
                fullWidth
              >
                {Vocabulary.scheduleTheoreticalExam}
              </Button>
            </Grid>
          )}

          <Grid item xs={12} sm={12} md={6} style={{ paddingTop: "10px" }}>
            <Button
              startIcon={<EventIcon />}
              variant="contained"
              color="success"
              onClick={() => {
                const isCertificate = getValues("product.isCertificate");
                const url = isCertificate
                  ? `${ExternalLinks.addInArrCertificate}?ID=1&ID2=${getValues("client_id.cnp")}&id=${getValues("_id")}`
                  : `${ExternalLinks.addInArr}?id=${getValues("_id")}`;
                window.open(url, "_blank");
              }}
              fullWidth
            >
              {Vocabulary.addInArr}
            </Button>
          </Grid>
        </>
      )}

      {hasReadiness && (
        <>
          <Divider sx={{ width: "100%", padding: "10px 0" }} />
          <Grid item xs={12} sm={12} md={12}>
            <Paper elevation={2} sx={{ padding: 2 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {Vocabulary.clientReadinessProgress}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={readinessProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor:
                          readinessProgress === 100 ? "#4caf50" : "#2196f3",
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    minWidth: "60px",
                    textAlign: "right",
                    color: readinessProgress === 100 ? "#4caf50" : "#2196f3",
                  }}
                >
                  {readinessProgress}%
                </Typography>
              </Box>

              {/* View Statistics Button */}
              <Button
                fullWidth
                variant="contained"
                color={openSetsDialog ? "secondary" : "primary"}
                size="medium"
                onClick={() => setOpenSetsDialog(!openSetsDialog)}
                sx={{
                  marginTop: 2,
                  color: (theme) =>
                    openSetsDialog
                      ? theme.palette.textColorSecondary?.main
                      : theme.palette.primary.contrastText,
                }}
              >
                {openSetsDialog
                  ? Vocabulary.hideStatistics
                  : Vocabulary.viewStatistics}
              </Button>

              {/* Sets List */}
              {openSetsDialog && clientReadinessData && (
                <Grid container spacing={1} sx={{ marginTop: 2 }}>
                  {Object.entries(clientReadinessData).map(
                    ([examCategory, examSets]) => {
                      if (!Array.isArray(examSets)) return null;

                      return examSets
                        .filter((set: any) => set.totalQuestions > 0)
                        .map((set: any) => {
                          const progress =
                            set.totalQuestions && set.answeredQuestions
                              ? Math.round(
                                  (set.answeredQuestions / set.totalQuestions) *
                                    100,
                                )
                              : 0;

                          return (
                            <Grid item xs={12} sm={6} md={6} key={set._id}>
                              <Card
                                sx={{
                                  padding: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  backgroundColor: "#fff",
                                  border: "1px solid #e0e0e0",
                                  borderRadius: 1,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {set.name}
                                </Typography>

                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  sx={{ marginTop: 1 }}
                                >
                                  <Box flex={1}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={progress}
                                      sx={{
                                        height: 4,
                                        borderRadius: 2,
                                        backgroundColor: "#e0e0e0",
                                        "& .MuiLinearProgress-bar": {
                                          backgroundColor:
                                            progress === 100
                                              ? "#4caf50"
                                              : "#2196f3",
                                          borderRadius: 2,
                                        },
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    color="primary"
                                  >
                                    {progress}%
                                  </Typography>
                                </Box>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ marginTop: 0.5 }}
                                >
                                  {set.answeredQuestions || 0} /{" "}
                                  {set.totalQuestions}
                                </Typography>
                              </Card>
                            </Grid>
                          );
                        });
                    },
                  )}
                </Grid>
              )}
            </Paper>
          </Grid>
        </>
      )}

      <GenericModal
        open={openConfirmationModal}
        onClose={() => setOpenConfirmationModal(false)}
        children={
          <>
            <Typography fontSize={18} component="div">
              {Vocabulary.changeToAutomataText
                .split(".")
                .map((sentence: string, index: number) =>
                  sentence.trim() ? (
                    <Box key={index} mb={1}>
                      {index ? (
                        sentence.trim()
                      ) : (
                        <strong>{sentence.trim()} </strong>
                      )}
                      .
                    </Box>
                  ) : null,
                )}
            </Typography>
          </>
        }
        title={Vocabulary.changeToAutomataConfirmationText}
        actions={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setOpenConfirmationModal(false)}
            >
              {Vocabulary.cancel}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={async () => {
                await updateClientToAutomata();
                setOpenConfirmationModal(false);
              }}
            >
              {Vocabulary.confirm}
            </Button>
          </div>
        }
      ></GenericModal>
    </Grid>
  );
}

export default withRole(ClientPanel);
